import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import MyEditor from '../MyEditor';
import getCookie from '../../utils/getCookies';
import {
  DocumentoSolicitacaoPreview,
  getPreVisualizarDocumentosSolicitacao,
} from '../../services/form-flow';

type Props = {
  onContinue: () => void;
  onBack: () => void;
};

const useStyles = makeStyles({
  title: {
    fontFamily: 'Roboto',
    fontWeight: 500,
    color: '#264259',
    textTransform: 'uppercase',
    textAlign: 'center',
    fontSize: 16,
    padding: 20,
  },
  subtitle: {
    color: '#264259',
    opacity: 0.7,
    textAlign: 'center',
    fontSize: 14,
    marginBottom: 16,
  },
  navLabel: {
    color: '#264259',
  },
  navRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 24,
    gap: 12,
  },
  buttonBack: {
    color: '#264259',
  },
  buttonPrimary: {
    backgroundColor: '#264259',
    color: '#fff',
  },
  editorBox: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    padding: 8,
    minHeight: 280,
  },
});

const DocumentPreviewSolicitacao: React.FC<Props> = ({ onContinue, onBack }) => {
  const classes = useStyles();
  const onContinueRef = useRef(onContinue);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [documentos, setDocumentos] = useState<DocumentoSolicitacaoPreview[]>([]);
  const [indiceAtual, setIndiceAtual] = useState(0);
  const [vistos, setVistos] = useState<Record<string, boolean>>({});

  useEffect(() => {
    onContinueRef.current = onContinue;
  }, [onContinue]);

  useEffect(() => {
    let cancelled = false;
    const carregar = async () => {
      const formStarted = JSON.parse(getCookie('gov_user_form_started') || '{}');
      const solicitacaoId = formStarted?.id;
      if (!solicitacaoId) {
        if (!cancelled) {
          setError('Não foi possível identificar a solicitação para pré-visualização.');
          setLoading(false);
        }
        return;
      }
      try {
        if (!cancelled) {
          setLoading(true);
          setError('');
        }
        const { data } = await getPreVisualizarDocumentosSolicitacao(solicitacaoId);
        const results = data?.results || [];
        if (cancelled) {
          return;
        }
        if (results.length === 0) {
          onContinueRef.current();
          return;
        }
        setDocumentos(results);
        setIndiceAtual(0);
        setVistos({ [results[0].id]: true });
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError('Erro ao carregar a pré-visualização dos documentos.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    carregar();
    return () => {
      cancelled = true;
    };
  }, []);

  const documentoAtual = documentos[indiceAtual];
  const total = documentos.length;
  const todosVistos = useMemo(() => {
    if (total === 0) {
      return false;
    }
    return documentos.every((doc) => Boolean(vistos[doc.id]));
  }, [documentos, total, vistos]);

  const irPara = (novoIndice: number) => {
    if (novoIndice < 0 || novoIndice >= total) {
      return;
    }
    const doc = documentos[novoIndice];
    setIndiceAtual(novoIndice);
    setVistos((atual) => ({ ...atual, [doc.id]: true }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" padding={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding={2}>
        <Typography color="error" align="center">
          {error}
        </Typography>
        <Box className={classes.actions}>
          <Button className={classes.buttonBack} onClick={onBack}>
            VOLTAR
          </Button>
        </Box>
      </Box>
    );
  }

  if (!documentoAtual) {
    return null;
  }

  return (
    <Box>
      <Typography className={classes.title}>Pré-visualização do documento</Typography>
      <Typography className={classes.subtitle}>
        Revise o(s) documento(s) de solicitação antes de assinar. Não é possível editar o conteúdo.
      </Typography>
      <Box className={classes.navRow}>
        <Button
          variant="outlined"
          color="primary"
          disabled={indiceAtual === 0}
          onClick={() => irPara(indiceAtual - 1)}
        >
          Anterior
        </Button>
        <Typography className={classes.navLabel}>
          Documento {indiceAtual + 1} de {total}
          {documentoAtual.assunto ? ` — ${documentoAtual.assunto}` : ''}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          disabled={indiceAtual >= total - 1}
          onClick={() => irPara(indiceAtual + 1)}
        >
          Próximo
        </Button>
      </Box>
      <Box className={classes.editorBox}>
        <MyEditor
          id={`preview-solicitacao-${documentoAtual.id}`}
          value={documentoAtual.conteudo_html}
          disabled
          modeEdit="basic"
          addConf={{
            toolbar: false,
            menubar: false,
            statusbar: false,
            readonly: true,
          }}
        />
      </Box>
      {!todosVistos && total > 1 && (
        <Typography className={classes.subtitle} style={{ marginTop: 12 }}>
          Navegue por todos os documentos para habilitar a continuidade.
        </Typography>
      )}
      <Grid container className={classes.actions}>
        <Button className={classes.buttonBack} onClick={onBack}>
          VOLTAR
        </Button>
        <Button
          variant="contained"
          className={classes.buttonPrimary}
          disabled={!todosVistos}
          onClick={onContinue}
        >
          Continuar para assinatura
        </Button>
      </Grid>
    </Box>
  );
};

export default DocumentPreviewSolicitacao;
